export type ReceivableTimingKind = "missing" | "due" | "overdue" | "upcoming";

export function receivableCompletionOn(project: {
  receivableCompletedOn?: string | null;
  constructionCompletedOn?: string | null;
}): string | null {
  return project.receivableCompletedOn ?? null;
}

export interface ReceivableTiming {
  kind: ReceivableTimingKind;
  days: number;
  label: string;
}

function dateOnlyParts(value: string): [number, number, number] | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return undefined;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function dateOnlyEpoch(value: string): number | undefined {
  const parts = dateOnlyParts(value);
  if (!parts) return undefined;
  return Date.UTC(parts[0], parts[1] - 1, parts[2]);
}

export function seoulCalendarDate(date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}

export function receivableTiming(completedOn?: string | null, todayOn = seoulCalendarDate()): ReceivableTiming {
  if (!completedOn) return { kind: "missing", days: 0, label: "수금일 미정" };
  const completed = dateOnlyEpoch(completedOn);
  const today = dateOnlyEpoch(todayOn);
  if (completed === undefined || today === undefined) return { kind: "missing", days: 0, label: "수금일 미정" };
  const days = Math.round((today - completed) / 86_400_000);
  if (days > 0) return { kind: "overdue", days, label: `미수금 ${days}일` };
  if (days === 0) return { kind: "due", days: 0, label: "오늘 수금" };
  return { kind: "upcoming", days: Math.abs(days), label: "수금 예정" };
}

function koreanDate(value: string): string {
  const parts = dateOnlyParts(value);
  if (!parts) return value;
  return `${parts[1]}월 ${parts[2]}일`;
}

function won(value: number): string {
  return `${Math.max(0, Math.round(value)).toLocaleString("ko-KR")}원`;
}

export function buildReceivableSms(input: {
  customerName: string;
  completedOn: string;
  outstandingAmount: number;
  quoteUrl: string;
  completionPhotoUrl?: string | null;
  depositBank: string;
  depositAccountNumber: string;
  accountHolder: string;
  managerName: string;
  managerPhone: string;
}): string {
  return [
    `${input.customerName}님,`,
    `${koreanDate(input.completedOn)} 작업이 완료되었습니다.`,
    "",
    `입금액: ${won(input.outstandingAmount)}`,
    `계좌: ${input.depositBank} ${input.depositAccountNumber} (${input.accountHolder})`,
    "",
    ...(input.completionPhotoUrl?.trim() ? [`완료 사진: ${input.completionPhotoUrl.trim()}`] : []),
    `견적서: ${input.quoteUrl}`,
    "",
    "※ 본 문자는 발신 전용입니다. 회신은 확인되지 않습니다.",
    "문의는 아래 담당자에게 연락해 주세요.",
    `문의: ${input.managerName} ${input.managerPhone}`,
  ].join("\n");
}

export type ReceivableCollectionTab = "waiting" | "completed" | "target";
export function receivableCollectionTab(project: {
  receivableCompletedOn?: string | null;
  outstandingAmount?: number;
}, todayOn = seoulCalendarDate()): ReceivableCollectionTab {
  if (project.outstandingAmount !== undefined && project.outstandingAmount <= 0) return "completed";
  const timing = receivableTiming(receivableCompletionOn(project), todayOn);
  return timing.kind === "due" || timing.kind === "overdue" ? "target" : "waiting";
}

export function receivableManagers(project: {
  receivableConstructionManagers?: Array<{ id: string; name: string }>;
  constructionManagerId?: string;
  constructionManagerName?: string;
}): Array<{ id: string; name: string }> {
  if (project.receivableConstructionManagers?.length) return project.receivableConstructionManagers;
  return project.constructionManagerId && project.constructionManagerName
    ? [{ id: project.constructionManagerId, name: project.constructionManagerName }] : [];
}
